import { useActiveAccount } from "@/hooks/useActiveAccount";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  Save,
  ShieldCheck,
  AlertTriangle,
  Smartphone,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  MessageSquare,
  GalleryHorizontal,
  Megaphone,
  ReceiptText,
  KeyRound,
} from "lucide-react";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { EditorSteps } from "@/components/whatsapp/WhatsAppUi";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { SearchableSelect } from "@/components/SearchableSelect";
import { WHATSAPP_LANGUAGES } from "@/lib/whatsapp-languages";
import { FormField } from "@/components/FormField";
import { Card, SectionHeader } from "@/components/Card";
import { HeaderEditor } from "@/components/templates/HeaderEditor";
import { BodyEditor } from "@/components/templates/BodyEditor";
import { FooterEditor } from "@/components/templates/FooterEditor";
import { ButtonsEditor } from "@/components/templates/ButtonsEditor";
import { CardsEditor } from "@/components/templates/CardsEditor";
import { PreviewSamplesEditor } from "@/components/templates/PreviewSamplesEditor";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { AiAssistModal } from "@/components/templates/AiAssistModal";
import { StatusBadge } from "@/components/templates/StatusBadge";
import {
  AuthenticationEditor,
  buildAuthPreviewBody,
} from "@/components/templates/AuthenticationEditor";
import {
  templatesService,
  Template,
  TemplateButton,
  TemplateCard,
  TemplateType,
  HeaderType,
  TemplateCategory,
  TemplateStatus,
  TemplateLocation,
  AiAssistResponse,
} from "@/services/templates.service";
import toast from "react-hot-toast";

const empty = {
  name: "",
  language: "en",
  category: "marketing" as TemplateCategory,
  type: "STANDARD" as TemplateType,
  headerType: "NONE" as HeaderType,
  headerText: "",
  headerImageUrl: "",
  headerVideoUrl: "",
  headerDocumentUrl: "",
  headerDocumentFilename: "",
  headerLocation: undefined as TemplateLocation | undefined,
  body: "",
  footer: "",
  buttons: [] as TemplateButton[],
  cards: [] as TemplateCard[],
  previewSamples: {} as Record<string, string>,
  useCases: [] as string[],
};

function extractVarNames(...sources: Array<string | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    if (!source) continue;
    source.replace(
      /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g,
      (_m, n: string) => {
        if (!seen.has(n)) {
          seen.add(n);
          out.push(n);
        }
        return _m;
      },
    );
  }
  return out;
}

function templateToForm(t: Template): typeof empty {
  return {
    name: t.name,
    language: t.language,
    category: t.category,
    type: t.type ?? "STANDARD",
    headerType: t.headerType ?? "NONE",
    headerText: t.headerText ?? "",
    headerImageUrl: t.headerImageUrl ?? "",
    headerVideoUrl: t.headerVideoUrl ?? "",
    headerDocumentUrl: t.headerDocumentUrl ?? "",
    headerDocumentFilename: t.headerDocumentFilename ?? "",
    headerLocation: t.headerLocation,
    body: t.body,
    footer: t.footer ?? "",
    buttons: t.buttons ?? [],
    cards: t.cards ?? [],
    previewSamples: t.previewSamples ?? {},
    useCases: t.useCases ?? [],
  };
}

export default function TemplateEditor() {
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicateFrom = searchParams.get("duplicateFrom");
  const editing = !!uid;
  const { active: activeAccount, accounts } = useActiveAccount();
  const [draftAccount, setDraftAccount] = useState<typeof activeAccount>(null);
  useEffect(() => { if (!draftAccount && activeAccount) setDraftAccount(activeAccount); }, [activeAccount, draftAccount]);
  const [form, setForm] = useState(empty);
  const [existing, setExisting] = useState<Template | null>(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const languageRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!editing) return;
    templatesService.get(uid!).then((t) => {
      setExisting(t);
      setForm(templateToForm(t));
    });
  }, [editing, uid]);

  // Duplicate-as-language-variant: prefill everything from the source template
  // but clear the language so the user picks the new one — Meta allows the same
  // template name when the language differs. `existing` stays null, so the copy
  // is a fresh DRAFT created on save.
  useEffect(() => {
    if (editing || !duplicateFrom) return;
    templatesService.get(duplicateFrom).then((t) => {
      setForm({ ...templateToForm(t), language: "" });
      languageRef.current?.focus();
    });
  }, [editing, duplicateFrom]);

  // Deep-link prefill from AI Studio's "Use asset" menu: /templates/new with
  // ?headerImageUrl= or ?headerVideoUrl= preselects the header type + media.
  useEffect(() => {
    if (editing || duplicateFrom) return;
    const headerImageUrl = searchParams.get("headerImageUrl");
    const headerVideoUrl = searchParams.get("headerVideoUrl");
    if (headerImageUrl)
      setForm((s) => ({ ...s, headerType: "IMAGE", headerImageUrl }));
    else if (headerVideoUrl)
      setForm((s) => ({ ...s, headerType: "VIDEO", headerVideoUrl }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variables = useMemo(() => {
    const fromBody = extractVarNames(form.body);
    const fromHeader =
      form.headerType === "TEXT" ? extractVarNames(form.headerText ?? "") : [];
    // Carousel: also walk per-card bodies so previewSamples auto-fills card vars too.
    const fromCards =
      form.type === "CAROUSEL"
        ? form.cards.flatMap((c) => extractVarNames(c.body ?? ""))
        : [];
    return Array.from(new Set([...fromBody, ...fromHeader, ...fromCards]));
  }, [form.body, form.headerText, form.headerType, form.type, form.cards]);

  // Suggested defaults for common parameter names. Used to auto-populate
  // previewSamples whenever a new variable appears in body/header.
  const SAMPLE_DEFAULTS: Record<string, string> = useMemo(
    () => ({
      parameter_1: "Sample 1",
      parameter_2: "Sample 2",
      parameter_3: "Sample 3",
      parameter_4: "Sample 4",
      parameter_5: "Sample 5",
      firstName: "Ali",
      lastName: "Khan",
      name: "Ali Khan",
      email: "ali@example.com",
      phone: "+923171755276",
      company: "Mi Coders",
      city: "Islamabad",
      country: "Pakistan",
      orderId: "#A8472",
      amount: "$129.00",
      code: "184302",
      otp: "184302",
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
    }),
    [],
  );

  // Auto-fill missing sample values whenever variables change. Users no longer
  // need to manually scroll to PreviewSamplesEditor for every new parameter —
  // a default appears immediately, and they can refine it inline.
  useEffect(() => {
    if (variables.length === 0) return;
    const missing = variables.filter((v) => !(v in form.previewSamples));
    if (missing.length === 0) return;
    const next = { ...form.previewSamples };
    for (const name of missing) {
      next[name] = SAMPLE_DEFAULTS[name] ?? `Example ${name}`;
    }
    setForm((s) => ({ ...s, previewSamples: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables.join("|")]);

  const set = <K extends keyof typeof empty>(k: K, v: (typeof empty)[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  /**
   * Build the payload sent to the backend. For authentication templates we
   * coerce the shape to what Meta accepts: no header (only NONE/TEXT allowed,
   * and even then the body is the OTP carrier — we always send `{{1}}` as the
   * body so Meta auto-fills the code, and we only allow COPY_CODE buttons).
   */
  const buildPayload = (): Parameters<typeof templatesService.create>[0] => {
    if (form.category === "authentication") {
      const expiry =
        form.footer && /\d/.test(form.footer) ? form.footer : undefined;
      return {
        name: form.name,
        language: form.language,
        category: "authentication",
        headerType: "NONE",
        body: "{{1}}",
        footer: expiry,
        buttons: form.buttons.filter((b) => b.type === "COPY_CODE"),
        previewSamples: {
          "1": form.previewSamples["1"] ?? "184302",
          // Preserve the security-disclaimer toggle (default ON if absent).
          __addSecurityRecommendation:
            form.previewSamples["__addSecurityRecommendation"] ?? "1",
        },
        useCases: form.useCases,
      };
    }
    if (form.type === "CAROUSEL") {
      return {
        name: form.name,
        language: form.language,
        category: form.category,
        type: "CAROUSEL",
        headerType: "NONE",
        body: form.body,
        cards: form.cards,
        previewSamples: Object.fromEntries(
          variables.map((k) => [k, form.previewSamples[k] ?? ""]),
        ),
        useCases: form.useCases,
      };
    }
    return {
      name: form.name,
      language: form.language,
      category: form.category,
      type: "STANDARD",
      headerType: form.headerType,
      headerText: form.headerType === "TEXT" ? form.headerText : undefined,
      headerImageUrl:
        form.headerType === "IMAGE" ? form.headerImageUrl : undefined,
      headerVideoUrl:
        form.headerType === "VIDEO" ? form.headerVideoUrl : undefined,
      headerDocumentUrl:
        form.headerType === "DOCUMENT" ? form.headerDocumentUrl : undefined,
      headerDocumentFilename:
        form.headerType === "DOCUMENT"
          ? form.headerDocumentFilename
          : undefined,
      headerLocation:
        form.headerType === "LOCATION" ? form.headerLocation : undefined,
      body: form.body,
      footer: form.footer || undefined,
      buttons: form.buttons,
      previewSamples: Object.fromEntries(
        variables.map((k) => [k, form.previewSamples[k] ?? ""]),
      ),
      useCases: form.useCases,
    };
  };

  /** Persist whatever the user has and return the resulting template. */
  const persist = async (): Promise<Template | null> => {
    const payload = { ...buildPayload(), accountUid: existing?.accountUid ?? draftAccount?.accountUid };
    if (!payload.accountUid) { toast.error('Connect a WhatsApp account, then reopen the template editor.'); return null; }
    const savedUid = uid ?? existing?.templateUid;
    if (savedUid) {
      const r = await templatesService.update(savedUid, payload);
      setExisting(r);
      return r;
    }
    const r = await templatesService.create(payload);
    setExisting(r);
    return r;
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await persist();
      if (r) {
        toast.success(editing ? "Saved" : "Saved as draft");
        if (!editing) nav(`/templates/${r.templateUid}`, { replace: true });
      }
    } catch {
      /* toast shown */
    } finally {
      setBusy(false);
    }
  };

  /** One-click "Save + submit". Persists first, then sends to Meta. */
  const submitToMeta = async () => {
    if (form.type === "CAROUSEL") {
      if (form.category !== "marketing") {
        toast.error("Carousel templates must use Marketing.");
        return;
      }
      if (form.cards.length < 2 || form.cards.length > 10) {
        toast.error("Add 2–10 carousel cards before submitting.");
        return;
      }
      const noButton = form.cards.findIndex((card) => !card.buttons?.length);
      if (noButton >= 0) {
        toast.error(`Add at least one button to card ${noButton + 1}.`);
        return;
      }
      const layout = (card: TemplateCard) =>
        JSON.stringify([
          card.headerType ?? "IMAGE",
          !!card.body,
          (card.buttons ?? []).map((button) => button.type),
        ]);
      const inconsistent = form.cards.findIndex(
        (card) => layout(card) !== layout(form.cards[0]),
      );
      if (inconsistent >= 0) {
        toast.error(
          `Card ${inconsistent + 1} must use the same media and button types as card 1.`,
        );
        return;
      }
      const missing = form.cards.findIndex(
        (card) =>
          !(card.headerType === "VIDEO"
            ? card.headerVideoUrl
            : card.headerImageUrl),
      );
      if (missing >= 0) {
        toast.error(`Upload media for card ${missing + 1}.`);
        return;
      }
    }
    // Meta hard-rejects COPY_CODE buttons on UTILITY templates (subcode 2388180).
    if (
      form.category === "utility" &&
      form.buttons.some((b) => b.type === "COPY_CODE")
    ) {
      toast.error(
        "Utility templates cannot have a Copy code button — remove it, or switch the category to Marketing (coupons) / Authentication (OTP).",
      );
      return;
    }
    setBusy(true);
    try {
      const persisted = await persist();
      if (!persisted) return;
      const submitted = await templatesService.submit(persisted.templateUid);
      setExisting(submitted);
      toast.success(`Submitted to Meta · status ${submitted.status}`);
      if (!editing)
        nav(`/templates/${persisted.templateUid}`, { replace: true });
    } catch {
      /* toast shown by interceptor */
    } finally {
      setBusy(false);
    }
  };

  const syncFromMeta = async () => {
    if (!existing) return;
    setSyncing(true);
    try {
      const updated = await templatesService.syncOneFromMeta(
        existing.templateUid,
      );
      const changed = updated.status !== existing.status;
      setExisting(updated);
      toast.success(
        changed
          ? `Status: ${existing.status} → ${updated.status}`
          : `Still ${updated.status}`,
      );
    } catch {
      /* toast shown by interceptor */
    } finally {
      setSyncing(false);
    }
  };

  const applyAi = (r: AiAssistResponse) => {
    if (r.type === "CAROUSEL") {
      setForm((s) => ({
        ...s,
        type: "CAROUSEL",
        category: r.category,
        headerType: "NONE",
        headerText: "",
        body: r.body,
        footer: "",
        buttons: [],
        cards: r.cards ?? [],
        previewSamples: {
          ...s.previewSamples,
          ...(r.suggestedSampleValues ?? {}),
        },
      }));
    } else {
      setForm((s) => ({
        ...s,
        type: "STANDARD",
        category: r.category,
        headerType: r.headerType,
        headerText: r.headerText ?? "",
        body: r.body,
        footer: r.footer ?? "",
        buttons: r.buttons,
        previewSamples: {
          ...s.previewSamples,
          ...(r.suggestedSampleValues ?? {}),
        },
      }));
    }
    setAiOpen(false);
  };

  const status: TemplateStatus = existing?.status ?? "DRAFT";
  const isLocked = status === "PENDING" || status === "APPROVED" || existing?.parameterFormat === "NAMED";

  return (
    <>
      <WhatsAppHeader
        section="templates"
        title={editing ? form.name || "Edit template" : "New template"}
        description={
          editing
            ? "Refine your message and review its approval status."
            : draftAccount ? `Create a template for ${draftAccount.businessName} · ${draftAccount.displayPhoneNumber}` : "Connect a WhatsApp account to create templates."
        }
        back={
          <button
            onClick={() => nav("/templates")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Back to templates"
          >
            <ArrowLeft size={16} />
          </button>
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {existing && <StatusBadge status={existing.status} />}
            {existing && existing.status !== "DRAFT" && (
              <Button
                variant="secondary"
                size="md"
                onClick={syncFromMeta}
                disabled={busy || syncing}
                iconLeft={
                  <RefreshCw
                    size={14}
                    className={syncing ? "animate-spin" : ""}
                  />
                }
                title="Pull latest status from Meta"
              >
                Sync
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              onClick={() => setAiOpen(true)}
              disabled={isLocked}
              iconLeft={<Sparkles size={14} />}
            >
              Generate with AI
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={save}
              disabled={
                busy ||
                !form.name ||
                !form.language ||
                (form.category !== "authentication" && !form.body) ||
                isLocked
              }
              iconLeft={<Save size={14} />}
            >
              Save Draft
            </Button>
            <Button
              onClick={() => setStep(2)}
              disabled={
                busy ||
                !form.name ||
                !form.language ||
                (form.category !== "authentication" && !form.body) ||
                isLocked
              }
              iconLeft={<ShieldCheck size={14} />}
            >
              {status === "APPROVED" ? "Approved" : "Review template"}
            </Button>
          </div>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-8">
        <EditorSteps
          current={step}
          onChange={setStep}
          steps={[
            {
              label: "Set up",
              description: "Purpose, language & format",
              done: !!form.name && !!form.language,
            },
            {
              label: "Compose",
              description: "Content, media & actions",
              done: !!form.body || form.category === "authentication",
            },
            { label: "Review", description: "Sample values & approval" },
          ]}
        />
        {isLocked && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text-secondary">
            <ShieldCheck size={18} className="text-primary" />
            This template is {status.toLowerCase()}. Duplicate it to create a
            new version.
          </div>
        )}
        <div className="grid grid-cols-1 items-start gap-7 xl:grid-cols-[minmax(0,1fr)_380px]">
          {/* LEFT — form */}
          <div className="min-w-0 space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Step {step + 1} of 3
              </p>
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
                {
                  [
                    "Start with the essentials",
                    "Make the message yours",
                    "Ready for a final look?",
                  ][step]
                }
              </h2>
              <p className="mt-2 text-sm text-text-muted">
                {
                  [
                    "Give your template a purpose and choose how it will appear.",
                    "Add your content. See every change in the preview beside you.",
                    "Check your message and sample values before sending it for approval.",
                  ][step]
                }
              </p>
            </div>
            {existing?.parameterFormat === "NAMED" && (
              <div className="rounded-xl border border-border bg-surface-alt px-4 py-3 text-sm text-text-secondary">
                This template uses Meta’s named parameters. You can send it using its approved values, edit it in WhatsApp Manager and sync, or duplicate it to create an editable CRM draft.
              </div>
            )}
            <fieldset disabled={isLocked || busy} className="min-w-0 space-y-5">
              {existing?.rejectionReason && (
                <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3">
                  <AlertTriangle
                    size={16}
                    className="mt-0.5 shrink-0 text-danger"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-danger">
                      Rejected by Meta
                    </div>
                    <div className="text-text-secondary mt-0.5">
                      {existing.rejectionReason}
                    </div>
                  </div>
                </div>
              )}

              {step === 0 && (
                <Card padded={false} className="overflow-hidden">
                  <div className="border-b border-border px-5 py-4">
                    <SectionHeader
                      title="Template details"
                      subtitle="A clear name makes this message easy to find later."
                      className="mb-0"
                    />
                  </div>
                  <div className="px-5 py-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <FormField
                        label="WhatsApp account"
                        htmlFor="template-account"
                        required
                        hint={editing ? "Fixed after creation" : "The number this template belongs to"}
                      >
                        <SearchableSelect
                          id="template-account"
                          size="lg"
                          value={existing?.accountUid ?? draftAccount?.accountUid ?? ""}
                          onChange={(uid) => setDraftAccount(accounts.find((a) => a.accountUid === uid) ?? null)}
                          options={accounts.map((a) => ({
                            value: a.accountUid,
                            label: a.businessName || a.verifiedName || a.displayPhoneNumber,
                            description: `${a.displayPhoneNumber}${a.isActive ? "" : " · not in use"}`,
                            avatarUrl: a.profilePicUrl || a.metaProfilePicUrl,
                            initials: (a.businessName || a.verifiedName || "W").trim().slice(0, 1).toUpperCase(),
                            group: "WhatsApp numbers",
                            keywords: `${a.phoneNumberId} ${a.verifiedName ?? ""}`,
                          }))}
                          placeholder="Select WhatsApp Business Account"
                          searchPlaceholder="Search by name or number"
                          emptyMessage="No WhatsApp numbers connected yet."
                          disabled={editing}
                        />
                      </FormField>
                    </div>
                    <div>
                      <FormField
                        label="Template name"
                        htmlFor="template-name"
                        hint="Lowercase letters, numbers and underscores"
                        hintBelow
                        required
                      >
                        <Input
                          id="template-name"
                          value={form.name}
                          disabled={editing}
                          onChange={(e) =>
                            set(
                              "name",
                              e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9_]/g, ""),
                            )
                          }
                          placeholder="order_confirmation"
                        />
                      </FormField>
                    </div>
                    <FormField
                      label="Language"
                      required
                      hint={
                        duplicateFrom && !form.language
                          ? "Pick the language for this variant"
                          : undefined
                      }
                    >
                      <SearchableSelect
                        ref={languageRef}
                        id="template-language"
                        value={form.language}
                        onChange={(v) => set("language", v)}
                        options={WHATSAPP_LANGUAGES}
                        placeholder="Select language…"
                        searchPlaceholder="Search language or code"
                        disabled={editing}
                      />
                    </FormField>
                    <div className="sm:col-span-2">
                      <FormField label="Message purpose" required>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {(
                            [
                              {
                                value: "marketing",
                                label: "Marketing",
                                description: "Offers, updates and invitations",
                                Icon: Megaphone,
                              },
                              {
                                value: "utility",
                                label: "Utility",
                                description: "Orders, appointments and alerts",
                                Icon: ReceiptText,
                              },
                              {
                                value: "authentication",
                                label: "Authentication",
                                description: "One-time codes and verification",
                                Icon: KeyRound,
                              },
                            ] as const
                          ).map(({ value, label, description, Icon }) => (
                            <button
                              key={value}
                              type="button"
                              aria-pressed={form.category === value}
                              onClick={() => set("category", value)}
                              className={`rounded-xl border p-4 text-left transition-colors ${form.category === value ? "border-primary bg-primary-soft" : "border-border hover:bg-surface-alt"}`}
                            >
                              <Icon size={19} className="mb-3 text-primary" />
                              <span className="block text-sm font-semibold text-text-primary">
                                {label}
                              </span>
                              <span className="mt-1 block text-xs leading-relaxed text-text-muted">
                                {description}
                              </span>
                            </button>
                          ))}
                        </div>
                      </FormField>
                    </div>
                  </div>

                  {/* Chatbot use-case toggle — makes the template appear in the bot-flow editor's template picker when filtered. */}
                  <div className="px-5 pb-4">
                    <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-border bg-surface-alt/40 px-3 py-2.5 hover:border-primary/40 transition-colors">
                      <input
                        type="checkbox"
                        checked={form.useCases.includes("chatbot")}
                        onChange={(e) => {
                          const next = new Set(form.useCases);
                          if (e.target.checked) next.add("chatbot");
                          else next.delete("chatbot");
                          set("useCases", Array.from(next));
                        }}
                        className="mt-0.5 h-4 w-4 accent-primary"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-text-primary">
                          Use this template in chatbots
                        </div>
                        <div className="text-xs text-text-muted mt-0.5">
                          Make this message available when building an automated
                          conversation.
                        </div>
                      </div>
                    </label>
                  </div>
                  {form.category !== "authentication" && (
                    <div className="px-5 pb-4">
                      <FormField
                        label="Message format"
                        hint="Choose a single message or a collection of cards."
                        hintBelow
                      >
                        <div className="grid grid-cols-2 gap-3">
                          {(
                            [
                              { value: "STANDARD", label: "Single message" },
                              { value: "CAROUSEL", label: "Carousel" },
                            ] as const
                          ).map((opt) => {
                            const active = form.type === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => set("type", opt.value)}
                                disabled={editing}
                                aria-pressed={active}
                                className={`rounded-xl border p-4 text-left text-sm font-medium transition-all ${
                                  active
                                    ? "border-primary bg-primary-soft text-text-primary"
                                    : "border-border bg-surface text-text-muted hover:text-text-primary"
                                } disabled:opacity-60 disabled:cursor-not-allowed`}
                              >
                                {opt.value === "STANDARD" ? (
                                  <MessageSquare
                                    size={21}
                                    className="mb-3 text-primary"
                                  />
                                ) : (
                                  <GalleryHorizontal
                                    size={21}
                                    className="mb-3 text-primary"
                                  />
                                )}
                                <span className="block">{opt.label}</span>
                                <span className="mt-1 block text-xs font-normal text-text-muted">
                                  {opt.value === "STANDARD"
                                    ? "Text, media and action buttons"
                                    : "2–10 swipeable product or media cards"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </FormField>
                    </div>
                  )}
                  {form.category === "authentication" && (
                    <div className="mx-5 mb-4 flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2.5">
                      <AlertTriangle
                        size={14}
                        className="mt-0.5 shrink-0 text-info"
                      />
                      <div className="text-xs text-text-secondary">
                        Authentication templates have a fixed shape on Meta: no
                        header, the body is auto-filled with a generated OTP,
                        and the only allowed button is "Copy code". The fields
                        below are configured for you.
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  {form.category === "authentication" ? (
                    /* Auth-only minimal editor */
                    <AuthenticationEditor
                      form={form}
                      onChange={(p) => setForm((s) => ({ ...s, ...p }))}
                      onSwitchToUtility={() =>
                        setForm((s) => ({
                          ...s,
                          category: "utility" as TemplateCategory,
                          // Seed an editable OTP-style body (only when empty) so the
                          // user starts from working text with extra parameters.
                          body:
                            s.body ||
                            "Hi {{name}}, {{code}} is your verification code. It expires in 10 minutes. For your security, do not share this code with anyone.",
                          buttons: s.buttons.filter(
                            (b) => b.type !== "COPY_CODE",
                          ),
                        }))
                      }
                    />
                  ) : form.type === "CAROUSEL" ? (
                    <>
                      <BodyEditor
                        value={form.body}
                        onChange={(v) => set("body", v)}
                        externalUsedNames={() =>
                          extractVarNames(form.headerText, form.footer)
                        }
                      />
                      {variables.length > 0 && (
                        <div className="rounded-xl border border-info/30 bg-info-soft px-4 py-3 text-xs text-text-secondary">
                          <strong className="text-info">
                            {variables.length} parameter
                            {variables.length === 1 ? "" : "s"} detected in the
                            body.
                          </strong>{" "}
                          Personalize the main message and card bodies with
                          contact variables. Keep every card’s media and button
                          layout consistent.
                        </div>
                      )}
                      <CardsEditor
                        value={form.cards}
                        onChange={(v) => set("cards", v)}
                      />
                    </>
                  ) : (
                    <>
                      <HeaderEditor
                        type={form.headerType}
                        text={form.headerText}
                        imageUrl={form.headerImageUrl}
                        videoUrl={form.headerVideoUrl}
                        documentUrl={form.headerDocumentUrl}
                        documentFilename={form.headerDocumentFilename}
                        location={form.headerLocation}
                        onChange={(p) => setForm((s) => ({ ...s, ...p }))}
                        externalUsedNames={() =>
                          extractVarNames(form.body, form.footer)
                        }
                      />
                      <BodyEditor
                        value={form.body}
                        onChange={(v) => set("body", v)}
                        externalUsedNames={() =>
                          extractVarNames(form.headerText, form.footer)
                        }
                      />
                      {variables.length > 0 && (
                        <div className="rounded-xl border border-info/30 bg-info-soft px-4 py-3 text-xs text-text-secondary">
                          <strong className="text-info">
                            {variables.length} parameter
                            {variables.length === 1 ? "" : "s"} detected.
                          </strong>{" "}
                          Sample values are ready in the Review step. Refine
                          them to show how your message will read.
                        </div>
                      )}
                      <FooterEditor
                        value={form.footer}
                        onChange={(v) => set("footer", v)}
                      />
                      <ButtonsEditor
                        value={form.buttons}
                        onChange={(v) => set("buttons", v)}
                        category={form.category}
                      />
                    </>
                  )}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-5">
                  <Card className="!p-6 !shadow-none">
                    <SectionHeader
                      title="Template summary"
                      subtitle="This is the version you will send to Meta."
                    />
                    <dl className="grid grid-cols-2 gap-5 text-sm">
                      {[
                        ["Name", form.name || "Add a name"],
                        ["Language", form.language || "Choose a language"],
                        ["Purpose", form.category],
                        [
                          "Format",
                          form.category === "authentication"
                            ? "One-time code"
                            : form.type === "CAROUSEL"
                              ? `${form.cards.length} carousel cards`
                              : "Single message",
                        ],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-xs text-text-muted">{label}</dt>
                          <dd className="mt-1 break-words font-medium capitalize text-text-primary">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </Card>
                  <PreviewSamplesEditor
                    variables={variables}
                    values={form.previewSamples}
                    onChange={(v) => set("previewSamples", v)}
                  />
                  <Card className="!p-6 !shadow-none">
                    <div className="flex items-start gap-3">
                      <ShieldCheck
                        size={22}
                        className="shrink-0 text-primary"
                      />
                      <div>
                        <h3 className="font-semibold text-text-primary">
                          Send for approval
                        </h3>
                        <p className="mt-1 text-sm leading-relaxed text-text-muted">
                          Meta reviews your message before it can be used in
                          campaigns. Sample values help explain your variables;
                          each contact's information is chosen when sending.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </fieldset>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
              <Button
                variant="ghost"
                onClick={() => (step ? setStep(step - 1) : nav("/templates"))}
                iconLeft={<ArrowLeft size={16} />}
              >
                {step ? "Back" : "Cancel"}
              </Button>
              {step < 2 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  iconRight={<ArrowRight size={16} />}
                >
                  Continue to {step === 0 ? "compose" : "review"}
                </Button>
              ) : (
                <Button
                  onClick={submitToMeta}
                  disabled={
                    busy ||
                    isLocked ||
                    !form.name ||
                    !form.language ||
                    (form.category !== "authentication" && !form.body)
                  }
                  loading={busy}
                  iconLeft={<ShieldCheck size={16} />}
                >
                  Submit for approval
                </Button>
              )}
            </div>
          </div>

          {/* RIGHT — sticky preview */}
          <aside className="min-w-0 rounded-2xl border border-border bg-surface-alt/50 p-4 sm:p-6 xl:sticky xl:top-5">
            <div className="space-y-5">
              <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                <Smartphone size={15} /> Message preview{" "}
                <span className="ml-auto rounded-full bg-surface px-2 py-1 text-[10px] tracking-normal">
                  Live
                </span>
              </div>
              <TemplatePreview
                headerType={form.type === "CAROUSEL" ? "NONE" : form.headerType}
                headerText={form.headerText}
                headerImageUrl={form.headerImageUrl}
                headerVideoUrl={form.headerVideoUrl}
                headerDocumentUrl={form.headerDocumentUrl}
                headerDocumentFilename={form.headerDocumentFilename}
                headerLocation={form.headerLocation}
                body={
                  form.category === "authentication"
                    ? buildAuthPreviewBody(form.previewSamples)
                    : form.body
                }
                footer={form.type === "CAROUSEL" ? undefined : form.footer}
                buttons={
                  form.type === "CAROUSEL"
                    ? []
                    : form.category === "authentication"
                      ? form.buttons.filter((b) => b.type === "COPY_CODE")
                      : form.buttons
                }
                cards={form.type === "CAROUSEL" ? form.cards : undefined}
                samples={form.previewSamples}
                contactName="SlightorCRM"
              />
              <p className="text-center text-xs leading-relaxed text-text-muted">
                A preview of your customer’s experience.
                <br />
                Sample values are for illustration.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <AiAssistModal
        initialBrief={
          form.body
            ? `Improve this WhatsApp template. Preserve its intent, links and named variables. Existing draft: ${JSON.stringify({ body: form.body, headerText: form.headerText, category: form.category, cards: form.cards, buttons: form.buttons })}`
            : undefined
        }
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onApply={applyAi}
      />
    </>
  );
}
